import {
  BrandVariants,
  Theme,
  createLightTheme,
  createDarkTheme,
  ColorPaletteTokens,
  ColorTokens,
  tokens,
} from '@fluentui/react-components';
import { ExtendedTokens, ExtendedTheme } from './types';
import { shared } from './shared';
import { AdsUITokens, InternalTokens, TypographyTokens } from '../tokens';
import { generateFontToken } from '../utils';

// ////////////////////////////////////
//
// Cozy theme
//
// ////////////////////////////////////

export const themeLabel = 'Cozy';

// override brand tokens for current theme
export const brandVariants: BrandVariants = {
  10: `#061724`,
  20: `#082338`,
  30: `#0a2e4a`,
  40: `#0c3b5e`,
  50: `#0e4775`,
  60: `#0f548c`,
  70: `#115ea3`,
  80: `#0f6cbd`,
  90: `#2886de`,
  100: `#479ef5`,
  110: `#62abf5`,
  120: `#77b7f7`,
  130: `#96c6fa`,
  140: `#b4d6fa`,
  150: `#cfe4fa`,
  160: `#ebf3fc`,
};

const baseThemeLight = createLightTheme(brandVariants);
const baseThemeDark = createDarkTheme(brandVariants);

const internalTokens: InternalTokens = {
  fontSizeBase100: '10px',
  fontSizeBase200: '12px',
  fontSizeBase300: '14px',
  fontSizeBase400: '16px',
  fontSizeBase500: '20px',
  fontSizeBase600: '24px',
  fontSizeBase700: '28px',
  fontSizeBase800: '32px',
  fontSizeBase900: '40px',
  fontSizeBase1000: '68px',

  lineHeightBase100: '14px',
  lineHeightBase200: '16px',
  lineHeightBase300: '20px',
  lineHeightBase400: '22px',
  lineHeightBase500: '28px',
  lineHeightBase600: '32px',
  lineHeightBase700: '36px',
  lineHeightBase800: '40px',
  lineHeightBase900: '52px',
  lineHeightBase1000: '92px',

  spacingXXXL: '32px',
  spacingXXL: '24px',
  spacingXL: '20px',
  spacingL: '16px',
  spacingM: '12px',
  spacingMNudge: '10px',
  spacingS: '8px',
  spacingSNudge: '6px',
  spacingXS: '4px',
  spacingXXS: '2px',
};

const typographyTokens: TypographyTokens = {
  // Line Height
  adsui_typographyLineHeightBase100: internalTokens.lineHeightBase100,
  adsui_typographyLineHeightBase200: internalTokens.lineHeightBase200,
  adsui_typographyLineHeightBase300: internalTokens.lineHeightBase300,
  adsui_typographyLineHeightBase400: internalTokens.lineHeightBase400,
  adsui_typographyLineHeightBase500: internalTokens.lineHeightBase500,
  adsui_typographyLineHeightBase600: internalTokens.lineHeightBase600,
  adsui_typographyLineHeightBase700: internalTokens.lineHeightBase700,
  adsui_typographyLineHeightBase800: internalTokens.lineHeightBase800,
  adsui_typographyLineHeightBase900: internalTokens.lineHeightBase900,
  adsui_typographyLineHeightBase1000: internalTokens.lineHeightBase1000,

  // Font Size
  adsui_typographyFontSizeBase100: internalTokens.fontSizeBase100,
  adsui_typographyFontSizeBase200: internalTokens.fontSizeBase200,
  adsui_typographyFontSizeBase300: internalTokens.fontSizeBase300,
  adsui_typographyFontSizeBase400: internalTokens.fontSizeBase400,
  adsui_typographyFontSizeBase500: internalTokens.fontSizeBase500,
  adsui_typographyFontSizeBase600: internalTokens.fontSizeBase600,
  adsui_typographyFontSizeBase700: internalTokens.fontSizeBase700,
  adsui_typographyFontSizeBase800: internalTokens.fontSizeBase800,
  adsui_typographyFontSizeBase900: internalTokens.fontSizeBase900,
  adsui_typographyFontSizeBase1000: internalTokens.fontSizeBase1000,
};

const modeIndependentTokens: Partial<ExtendedTokens<AdsUITokens>> = {
  // Global Colors
  adsui_colorBackgroundDefault: tokens.colorNeutralBackgroundAlpha,
  adsui_colorBackgroundFilled: tokens.colorNeutralBackground2,
  adsui_colorBackgroundElevated: tokens.colorNeutralBackground1,

  // Radius
  adsui_radiusSmall: tokens.borderRadiusSmall,
  adsui_radiusMedium: tokens.borderRadiusMedium,
  adsui_radiusLarge: tokens.borderRadiusLarge,
  adsui_radiusXLarge: tokens.borderRadiusXLarge,

  // Spacing
  adsui_spacingXXS: internalTokens.spacingXXS,
  adsui_spacingXS: internalTokens.spacingXS,
  adsui_spacingSNudge: internalTokens.spacingSNudge,
  adsui_spacingS: internalTokens.spacingS,
  adsui_spacingMNudge: internalTokens.spacingMNudge,
  adsui_spacingM: internalTokens.spacingM,
  adsui_spacingL: internalTokens.spacingL,
  adsui_spacingXL: internalTokens.spacingXL,
  adsui_spacingXXL: internalTokens.spacingXXL,
  adsui_spacingXXXL: internalTokens.spacingXXXL,

  // Fonts
  adsui_typographyCaption2: generateFontToken({
    fontWeight: tokens.fontWeightRegular,
    fontSize: typographyTokens.adsui_typographyFontSizeBase100,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase100,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyCaption2Strong: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase100,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase100,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyCaption1: generateFontToken({
    fontWeight: tokens.fontWeightRegular,
    fontSize: typographyTokens.adsui_typographyFontSizeBase200,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase200,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyCaption1Strong: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase200,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase200,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyCaption1Stronger: generateFontToken({
    fontWeight: tokens.fontWeightBold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase200,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase200,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyBody1: generateFontToken({
    fontWeight: tokens.fontWeightRegular,
    fontSize: typographyTokens.adsui_typographyFontSizeBase300,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase300,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyBody1Strong: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase300,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase300,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyBody1Stronger: generateFontToken({
    fontWeight: tokens.fontWeightBold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase300,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase300,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyBody2: generateFontToken({
    fontWeight: tokens.fontWeightRegular,
    fontSize: typographyTokens.adsui_typographyFontSizeBase400,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase400,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographySubtitle2: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase400,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase400,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographySubtitle2Stronger: generateFontToken({
    fontWeight: tokens.fontWeightBold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase400,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase400,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographySubtitle1: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase500,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase500,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyTitle3: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase600,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase600,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyTitle2: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase700,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase700,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyTitle1: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase800,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase800,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyLargeTitle: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase900,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase900,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyDisplay: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase1000,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase1000,
    fontFamily: tokens.fontFamilyBase,
  }),

  adsui_typographyPageTitle: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase700,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase700,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographyPageSubheader: generateFontToken({
    fontWeight: tokens.fontWeightRegular,
    fontSize: typographyTokens.adsui_typographyFontSizeBase400,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase400,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographySectionTitle: generateFontToken({
    fontWeight: tokens.fontWeightSemibold,
    fontSize: typographyTokens.adsui_typographyFontSizeBase500,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase500,
    fontFamily: tokens.fontFamilyBase,
  }),
  adsui_typographySectionSubheader: generateFontToken({
    fontWeight: tokens.fontWeightRegular,
    fontSize: typographyTokens.adsui_typographyFontSizeBase300,
    lineHeight: typographyTokens.adsui_typographyLineHeightBase300,
    fontFamily: tokens.fontFamilyBase,
  }),
};

// override component tokens for current theme
const adsUITokensLight: ExtendedTokens<AdsUITokens> = {
  ...shared.componentTokensLight,
  ...modeIndependentTokens,
};

const adsUITokensDark: ExtendedTokens<AdsUITokens> = {
  ...shared.componentTokensDark,
  ...modeIndependentTokens,
};

// override base fluent theme tokens for light theme
const colorTokensLight: ExtendedTokens<ColorTokens> = {
  ...baseThemeLight,
  ...shared.colorTokensLight,
};

// override tokens for Dark theme
const colorTokensDark: ExtendedTokens<ColorTokens> = {
  ...baseThemeDark,
  ...shared.colorTokensDark,
};

// override base fluent theme pallete tokens for light and dark theme
const colorPalletTokensLight: ExtendedTokens<ColorPaletteTokens> = {
  ...baseThemeLight,
};

const colorPalletTokensDark: ExtendedTokens<ColorPaletteTokens> = {
  ...baseThemeDark,
};

export const themeLight = {
  ...colorTokensLight,
  ...colorPalletTokensLight,
  ...adsUITokensLight,
} as ExtendedTheme<Theme>;

export const themeDark = {
  ...colorTokensDark,
  ...colorPalletTokensDark,
  ...adsUITokensDark,
} as ExtendedTheme<Theme>;
